import { useEffect, useRef, useState } from "react";
import { useLockersContext } from "../contexts/LockersContext";
import { useSpecificLockerActionContext } from "../contexts/SpecificLockerActionContext";
import { FavouriteLockerIntegratorOption, FavouriteLockerType, LocalStorageFavouriteLocker, SubscriptionType } from "../data/LockerDataTypes";
import { has3rdPartyCookiesEnabled } from "../utils/ThirdPartyCookies";
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { BriefCase, FavouriteLockerHeart, HouseHeart } from "./Miscellaneous";
import { useTranslation } from "react-i18next";
import { useTrackingContext } from "../contexts/TrackingContext";
import { TrackedFavouriteLocker } from "../data/Tracking";
import { useClientContext } from "../contexts/ClientContext";
import { LockerService } from "../services/lockerService";
import { Api } from "../utils/Api";
import { FAV_LOCKER_KEY } from "../Settings";

const FavouriteLockerComponent = () => {
    const { lockerPluginOptions } = useLockersContext();
    const [isFavouriteLocker, setIsFavouriteLocker] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const { t } = useTranslation();
    const {
        selectedLocker,
        favouriteLockers,
        previouslyUsedLockers,
        setPreviouslyUsedLockers,
        setFavouriteLockers    
    } = useSpecificLockerActionContext();
    const {
        setFavouriteLockerActioned
    } = useTrackingContext();
    const {
        clientHasFavouriteLockerEnabled,
        setClientHasFavouriteLockerEnabled
    } = useClientContext();
    let clientHasFavouriteLockerEnabledRef = useRef(clientHasFavouriteLockerEnabled)

    useEffect(()=>{
        const isSelectedLockerFavourite = favouriteLockers && favouriteLockers?.length > 0 && favouriteLockers.find(x => x.lockerId == selectedLocker.lockerId);
        if (isSelectedLockerFavourite) {
            setIsFavouriteLocker(true);
        }
    },[])

    useEffect(() => {
        clientHasFavouriteLockerEnabledRef.current = clientHasFavouriteLockerEnabled;
    }, [clientHasFavouriteLockerEnabled])

    useEffect(()=>{
        SaveFavouriteLockersToLocalStorage(favouriteLockers.filter(x => x.type != FavouriteLockerType.Unknown));
    },[favouriteLockers])

    const handleOnDelete = () => {
        setShowPopover(false);
        deleteFavouriteLocker();
    }

    const handleOnSelect = () => {
        setShowPopover(true)
    }

    const SaveFavouriteLockersToLocalStorage = (newFavouriteLockers: LocalStorageFavouriteLocker[]): void => {
        if (has3rdPartyCookiesEnabled()) {
            localStorage.setItem(FAV_LOCKER_KEY!.concat(lockerPluginOptions.countryCode), JSON.stringify(newFavouriteLockers));
        }
    }
    
    const ClearFavouriteLockersFromLocalStorage = (): void => {
        if (has3rdPartyCookiesEnabled()) {
            localStorage.removeItem(FAV_LOCKER_KEY!.concat(lockerPluginOptions.countryCode));
        }
    }

    const InformIntegratorAboutFavouriteLocker = (objectToPush: any) => {
        switch (clientHasFavouriteLockerEnabledRef.current) {
            case FavouriteLockerIntegratorOption.Disabled: return;
            case FavouriteLockerIntegratorOption.Enabled: return PushFavouriteLockerToIntegrator(objectToPush);
            case FavouriteLockerIntegratorOption.Unknown: return SetIntegratorFavouriteLockerOption(objectToPush);
            default: return;
        }
    }

    const PushFavouriteLockerToIntegrator = (objectToPush: any): void => {
        const response = LockerService.getPostMessage(SubscriptionType.FavouriteLocker, objectToPush);
        window?.parent?.postMessage(response, '*');
    }

    const SetIntegratorFavouriteLockerOption = (objectToPush: any): void => {
        Api.Get().HasFavouriteLockerEnabled(lockerPluginOptions.clientId).then(isFavouriteLockerEnabled => {
            setClientHasFavouriteLockerEnabled(isFavouriteLockerEnabled ? 
                FavouriteLockerIntegratorOption.Enabled : FavouriteLockerIntegratorOption.Disabled)
            InformIntegratorAboutFavouriteLocker(objectToPush)
        })
    }
    
    const SaveNewFavouriteLocker = (newFavouriteLocker: LocalStorageFavouriteLocker, type: FavouriteLockerType): void => {
        let newFavourite = {
            lockerId: newFavouriteLocker.lockerId,
            lat: newFavouriteLocker.lat,
            lng: newFavouriteLocker.lng,
            type: type
        } as LocalStorageFavouriteLocker;
    
        let newFavouriteLockers = favouriteLockers.filter(x => x.type != newFavourite.type);
        newFavouriteLockers.push(newFavourite);
        setFavouriteLockers(newFavouriteLockers);
    }

    const RemoveFromPreviouslyUsedLockers=(newFavourite: LocalStorageFavouriteLocker):void=>{
        let usedLockers = previouslyUsedLockers.filter(obj => obj && obj.lockerId !== newFavourite.lockerId);
        setPreviouslyUsedLockers(usedLockers);
    }
    
    const handleFavouriteLocker = (type: FavouriteLockerType): void => {
        let newFavourite = {
            lockerId: selectedLocker.lockerId,
            lat: selectedLocker.lat,
            lng: selectedLocker.lng,
            type: type
        } as LocalStorageFavouriteLocker;

        SaveNewFavouriteLocker(newFavourite, type);        
        InformIntegratorAboutFavouriteLocker({ lockerId: newFavourite.lockerId });
        setFavouriteLockerActioned({ lockerId: selectedLocker.lockerId, isFavouriteLocker: true, favouriteLockerType: type } as TrackedFavouriteLocker);
        
        RemoveFromPreviouslyUsedLockers(newFavourite);
        setIsFavouriteLocker(true);
        setShowPopover(false);
    }

    const deleteFavouriteLocker = (): void => {
        setFavouriteLockerActioned({ lockerId: selectedLocker.lockerId, isFavouriteLocker: false, favouriteLockerType: favouriteLockers?.find(fl => fl.lockerId === selectedLocker.lockerId)?.type } as TrackedFavouriteLocker);
        let favouriteLockersFiltered = favouriteLockers?.filter(x => x.lockerId !== selectedLocker.lockerId);
        ClearFavouriteLockersFromLocalStorage();
        SaveFavouriteLockersToLocalStorage(favouriteLockersFiltered);
        setFavouriteLockers(favouriteLockersFiltered);
        InformIntegratorAboutFavouriteLocker({});
        setIsFavouriteLocker(false);
    }

    const FavouriteLockerButton=(props:any): any =>{
        return(
             <Button className="btn btn-sm vertical-center locker-plugin-favourite-btn mb-1"
             onClick={(event: any) => { handleFavouriteLocker(props.lockerType); event.stopPropagation() }}>
                 {props.icon}
                 <span className="a-bit-of-margin-left">
                     {t(props.label)}
                 </span>
             </Button>
         ) 
     }
     
    const popover = (!isFavouriteLocker ?
        <Popover id="popover-basic" className="fav-popover">
            <Popover.Body>
                <div className='locker-plugin-place-selector-btn-container mx-auto'>
                    <FavouriteLockerButton lockerType={FavouriteLockerType.Home} icon={<HouseHeart/>} label={"home"}/>
                    <FavouriteLockerButton lockerType={FavouriteLockerType.Work} icon={<BriefCase/>} label={"work"}/>
                </div>
            </Popover.Body>
        </Popover> : <></>
    );

    return (
       <>{has3rdPartyCookiesEnabled() &&
            <OverlayTrigger
                show={showPopover}
                key={"top"}
                placement={"top"}
                overlay={popover}>
                <Button variant="secondary" className={isFavouriteLocker ? "btn btn-sm locker-plugin-favourite-btn loved mb-1" : "btn btn-sm locker-plugin-favourite-btn mb-1"}
                    onClick={(event: any) => { isFavouriteLocker ? handleOnDelete() : handleOnSelect(); event.stopPropagation() }}>
                    <FavouriteLockerHeart />
                    <span className="a-bit-of-margin-left">
                        {isFavouriteLocker ? t("removeFavouriteLocker") : t("favouriteLocker")}
                    </span>
                </Button>
            </OverlayTrigger>
        }</>
    );
}
export default FavouriteLockerComponent;